import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { noop } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { ButtonTrigger } from 'venn-ui-kit';
import type { LibraryFilterOption } from './types';
import { cn } from '@/lib/utils';
import { filterMenuItemStyle } from './shared/constants';

interface ItemTypeFilterProps {
  /** The options for the filter */
  items: LibraryFilterOption<string>[];
  label: string;
  onFilter: (updated: string) => void;
}

/**
 * Component for a simple library filter, which includes a trigger button and a dropdown menu
 */
export const ItemTypeFilter = ({ items, label: itemTypeLabel, onFilter }: ItemTypeFilterProps) => {
  const [open, setOpen] = React.useState(false);
  const toMenuItem = useCallback(
    ({ icon, label, subItems, value, className }: LibraryFilterOption<string>) => {
      const hasSubItems = subItems && subItems.length > 0;
      return hasSubItems ? (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={cn(filterMenuItemStyle, className)} key={`itemType-${value}`}>
            {icon}
            {label}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="p-0">{subItems.map(toMenuItem)}</DropdownMenuSubContent>
        </DropdownMenuSub>
      ) : (
        <DropdownMenuItem
          key={`itemType-${value}`}
          onSelect={() => onFilter(value)}
          className={cn(filterMenuItemStyle, className)}
        >
          {icon}
          {label}
        </DropdownMenuItem>
      );
    },
    [onFilter],
  );
  const dropmenuItems = useMemo(() => items.map(toMenuItem), [items, toMenuItem]);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger className="qa-library-filter-trigger" aria-label="Item type filter dropdown menu button">
        <ButtonTrigger label={itemTypeLabel} isOpen={open} onClick={noop} aria-expanded={open} aria-haspopup />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="p-0">
        {dropmenuItems}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
